import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, CircularProgress, Alert, Button } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { paymentService, Payment } from '../services/payment';
import { useSubscription } from '../contexts/SubscriptionContext';

interface PaymentSuccessPageProps {
  onBackToHome: () => void;
}

export const PaymentSuccessPage: React.FC<PaymentSuccessPageProps> = ({ onBackToHome }) => {
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [error, setError] = useState<string>('');
  const { checkSubscription } = useSubscription();

  useEffect(() => {
    const completePayment = async () => {
      try {
        // Get payment and payer IDs from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const paymentId = urlParams.get('paymentId');
        const payerId = urlParams.get('PayerID');

        if (!paymentId || !payerId) {
          setError('Invalid payment parameters');
          setLoading(false);
          return;
        }

        // Execute the payment
        const completedPayment = await paymentService.completePayment(paymentId, payerId);
        setPayment(completedPayment);
        
        // 🔥 Reload subscription status after successful payment
        await checkSubscription();
        
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to complete payment');
        setLoading(false);
      }
    };

    completePayment();
  }, [checkSubscription]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ color: '#9b59b6' }} />
        <Typography variant="h5" sx={{ mt: 3, color: '#c0c0c0' }}>
          Processing your payment...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={onBackToHome}
          sx={{
            background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
            color: '#ffffff',
          }}
        >
          Back to Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
      <Box
        sx={{
          backgroundColor: '#1a1a1a',
          border: '2px solid #9b59b6',
          borderRadius: 2,
          p: 6,
        }}
      >
        <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 3 }} />
        
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: '#c0c0c0' }}>
          Payment Successful!
        </Typography>

        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Thank you for your purchase
        </Typography>

        {payment && (
          <Box sx={{ textAlign: 'left', mb: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              <strong>Product:</strong> {payment.product_name}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              <strong>Amount:</strong> ${payment.amount.toFixed(2)} {payment.currency}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              <strong>Payment ID:</strong> {payment.paypal_payment_id}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <strong>Status:</strong>{' '}
              <span style={{ color: '#4caf50', fontWeight: 600 }}>
                {payment.status.toUpperCase()}
              </span>
            </Typography>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          A confirmation email has been sent to your registered email address.
        </Typography>

        <Button
          variant="contained"
          size="large"
          onClick={onBackToHome}
          sx={{
            background: 'linear-gradient(135deg, #9b59b6 0%, #c74375 100%)',
            color: '#ffffff',
            px: 4,
            py: 1.5,
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #7d3c98 0%, #ad1457 100%)',
            },
          }}
        >
          Back to Home
        </Button>
      </Box>
    </Container>
  );
};
